import React, { useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  Menu, X, LogOut, LayoutDashboard, Users, UserCircle, Book, Settings, Key, 
  Star, MessageCircle, Shield, ChevronDown, CreditCard, FolderOpen,
  Palette, Heart, Volume2, Globe, FileText, HelpCircle
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const AdminSidebar = ({ children }) => {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [contentManagementOpen, setContentManagementOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    setUserMenuOpen(false);
  };

  const mainNavItems = [
    { label: 'Dashboard', to: '/admin', icon: LayoutDashboard },
    { label: 'Users', to: '/admin/users', icon: Users },
    { label: 'Profiles', to: '/admin/profiles', icon: UserCircle },
  ];

  const contentManagementItems = [
    { label: 'Stories', to: '/admin/stories', icon: Book },
    { label: 'Feedback', to: '/admin/feedbacks', icon: MessageCircle },
    { label: 'Reviews', to: '/admin/reviews', icon: Star },
    { label: 'Story Themes', to: '/admin/content/themes', icon: Palette },
    { label: 'Story Interests', to: '/admin/content/interests', icon: Heart },
    { label: 'Story Tones', to: '/admin/content/tones', icon: Volume2 },
    { label: 'Story Languages', to: '/admin/content/languages', icon: Globe },
    { label: 'Subscription Plans', to: '/admin/plans', icon: CreditCard },
    { label: 'Privacy Policy', to: '/admin/content/privacy', icon: FileText },
    { label: 'Terms & Conditions', to: '/admin/content/terms', icon: FileText },
    { label: 'Help Center', to: '/admin/content/help', icon: HelpCircle },
  ];

  const otherNavItems = [
    { label: 'Subscriptions', to: '/admin/subscriptions', icon: CreditCard },
    { label: 'API Keys', to: '/admin/api-keys', icon: Key },
  ];

  // Add Admin Users link only for superadmin
  const superAdminItems = admin?.role === 'superadmin' 
    ? [{ label: 'Admin Users', to: '/admin/admins', icon: Shield }]
    : [];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const isContentManagementActive = contentManagementItems.some(item => isActive(item.to));

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex">
      {/* Sidebar */}
      <aside
        className={`bg-white shadow-xl border-r border-gray-200 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        } flex flex-col fixed h-full z-40`}
      >
        {/* Logo Section */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <Link 
            to="/admin" 
            className={`flex items-center space-x-3 group transition-opacity ${!sidebarOpen && 'opacity-0 w-0 overflow-hidden'}`}
          >
            <div className="p-2 rounded-lg">
              <img src="/icon/512.png" alt="mySTORYme" className="h-5 w-5 object-contain" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-lg font-black tracking-tight leading-none whitespace-nowrap">
                <span className="text-[#ff405c]">my</span>
                <span className="text-[#5c3fd8]">STORY</span>
                <span className="text-[#ff405c]">me</span>
              </span>
              <span className="text-xs font-semibold text-gray-500 -mt-0.5">Admin Panel</span>
            </div>
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <X className="h-5 w-5 text-gray-600" /> : <Menu className="h-5 w-5 text-gray-600" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {/* Main Navigation */}
          <div className="space-y-1 mb-6">
            {mainNavItems.map(({ label, to, icon: Icon }) => {
              const active = isActive(to);
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/admin'}
                  className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
                    active
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                      : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  <Icon className={`h-5 w-5 flex-shrink-0 ${sidebarOpen ? 'mr-3' : 'mx-auto'}`} />
                  {sidebarOpen && <span>{label}</span>}
                </NavLink>
              );
            })}
          </div>

          {/* Content Management Section */}
          <div className="mb-6">
            <button
              onClick={() => setContentManagementOpen(!contentManagementOpen)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                isContentManagementActive
                  ? 'text-purple-700 bg-purple-50'
                  : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
              }`}
            >
              <div className="flex items-center">
                <FolderOpen className={`h-5 w-5 flex-shrink-0 ${sidebarOpen ? 'mr-3' : 'mx-auto'}`} />
                {sidebarOpen && <span>Content Management</span>}
              </div>
              {sidebarOpen && (
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${contentManagementOpen ? 'rotate-180' : ''}`}
                />
              )}
            </button>

            {contentManagementOpen && sidebarOpen && (
              <div className="mt-1 ml-4 space-y-1 border-l-2 border-purple-100 pl-3">
                {contentManagementItems.map(({ label, to, icon: Icon }) => {
                  const active = isActive(to);
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        active
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                          : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                      }`}
                    >
                      <Icon className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span>{label}</span>
                    </NavLink>
                  );
                })}
              </div>
            )}

            {/* Show content management items as icons when sidebar is collapsed */}
            {!sidebarOpen && (
              <div className="mt-1 space-y-1">
                {contentManagementItems.map(({ label, to, icon: Icon }) => {
                  const active = isActive(to);
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      className={`flex items-center justify-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        active
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                          : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                      }`}
                      title={label}
                    >
                      <Icon className="h-5 w-5" />
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>

          {/* Other Navigation */}
          <div className="space-y-1 mb-6">
            {otherNavItems.map(({ label, to, icon: Icon }) => {
              const active = isActive(to);
              return (
                <NavLink
                  key={to}
                  to={to}
                  className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
                    active
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                      : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  <Icon className={`h-5 w-5 flex-shrink-0 ${sidebarOpen ? 'mr-3' : 'mx-auto'}`} />
                  {sidebarOpen && <span>{label}</span>}
                </NavLink>
              );
            })}
          </div>

          {/* Super Admin Items */}
          {superAdminItems.length > 0 && (
            <div className="space-y-1 mb-6 border-t border-gray-200 pt-4">
              {superAdminItems.map(({ label, to, icon: Icon }) => {
                const active = isActive(to);
                return (
                  <NavLink
                    key={to}
                    to={to}
                    className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
                      active
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                        : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                    }`}
                  >
                    <Icon className={`h-5 w-5 flex-shrink-0 ${sidebarOpen ? 'mr-3' : 'mx-auto'}`} />
                    {sidebarOpen && <span>{label}</span>}
                  </NavLink>
                );
              })}
            </div>
          )}
        </nav>

        {/* User Section */}
        <div className="border-t border-gray-200 p-4">
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors group"
            >
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {admin?.firstName?.[0]?.toUpperCase() || 'A'}
              </div>
              {sidebarOpen && (
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {admin?.firstName} {admin?.lastName}
                  </div>
                  <div className="text-xs text-gray-500">
                    {admin?.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                  </div>
                </div>
              )}
              {sidebarOpen && (
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              )}
            </button>

            {userMenuOpen && sidebarOpen && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute bottom-full left-0 mb-2 w-full bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-20">
                  <Link
                    to="/admin/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Account Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {location.pathname === '/admin' && 'Dashboard'}
                  {location.pathname === '/admin/users' && 'Users'}
                  {location.pathname === '/admin/profiles' && 'Profiles'}
                  {location.pathname === '/admin/stories' && 'Stories'}
                  {location.pathname === '/admin/feedbacks' && 'Feedback'}
                  {location.pathname === '/admin/reviews' && 'Reviews'}
                  {location.pathname === '/admin/plans' && 'Plans'}
                  {location.pathname === '/admin/subscriptions' && 'Subscriptions'}
                  {location.pathname === '/admin/api-keys' && 'API Keys'}
                  {location.pathname === '/admin/admins' && 'Admin Users'}
                  {location.pathname === '/admin/profile' && 'Account Settings'}
                </h1>
              </div>
              <div className="flex items-center space-x-4">
                <Link
                  to="/admin/profile"
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Account Settings"
                >
                  <Settings className="h-5 w-5 text-gray-600" />
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminSidebar;

